'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bell, Search, Calendar, ChevronDown, Settings, LogOut, User, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

interface NotificationItem {
  id: string;
  title: string;
  body: string;
  type: string;
  is_read: boolean;
  link?: string | null;
  created_at: string;
}

interface UserProfile {
  displayName: string;
  initials: string;
  avatarUrl: string | null;
  role: string;
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Topbar() {
  const { user, loading: authLoading, signOut } = useAuth();
  const router = useRouter();
  const supabase = useRef(createClient()).current;

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [today, setToday] = useState('');

  const notifRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Set today's date client-side to avoid hydration mismatch
  useEffect(() => {
    const d = new Date();
    setToday(
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    );
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch role-based profile
  const fetchProfile = useCallback(async () => {
    if (!user) return;

    const role: string = user.user_metadata?.role ?? 'athlete';
    let displayName = '';
    let avatarUrl: string | null = null;

    if (role === 'coach') {
      const { data } = await supabase
        .from('coach_profiles')
        .select('name, profile_image_url')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) {
        displayName = data.name ?? '';
        avatarUrl = data.profile_image_url ?? null;
      }
    } else if (role === 'athlete') {
      const { data } = await supabase
        .from('athlete_profiles')
        .select('name, profile_image_url')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) {
        displayName = data.name ?? '';
        avatarUrl = data.profile_image_url ?? null;
      }
    }

    // Fallback: use auth metadata full_name, then email
    if (!displayName) {
      displayName =
        user.user_metadata?.full_name ||
        user.user_metadata?.name ||
        user.email?.split('@')[0] ||
        'User';
    }

    setProfile({
      displayName,
      initials: getInitials(displayName),
      avatarUrl,
      role,
    });
  }, [user, supabase]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('notifications')
      .select('id, title, body, type, is_read, link, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      // Silently handle — table may not exist yet or RLS may block
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Topbar] notifications fetch error:', error.message);
      }
      return;
    }

    const items: NotificationItem[] = data ?? [];
    setNotifications(items);
    setUnreadCount(items.filter((n) => !n.is_read).length);
  }, [user, supabase]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchProfile();
      fetchNotifications();
    }
    if (!authLoading && !user) {
      setProfile(null);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [authLoading, user, fetchProfile, fetchNotifications]);

  // Mark notification as read
  const markRead = async (id: string) => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  // Mark all read
  const markAllRead = async () => {
    if (!user) return;
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    await signOut();
    router.push('/login');
  };

  const handleNotifClick = (notif: NotificationItem) => {
    markRead(notif.id);
    if (notif.link) {
      router.push(notif.link);
      setNotifOpen(false);
    }
  };

  return (
    <header
      className="h-14 flex items-center justify-between px-6 flex-shrink-0 relative z-40"
      style={{
        background: 'rgba(10,10,10,0.85)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Left: Search */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            type="text"
            placeholder="Search athletes..."
            className="input-field pl-8 pr-3 py-1.5 w-56"
          />
        </div>
      </div>

      {/* Right: Date, Notifications, User */}
      <div className="flex items-center gap-3">
        {/* Date */}
        <div className="flex items-center gap-1.5 text-xs text-zinc-600 font-mono">
          <Calendar size={12} />
          <span>{today}</span>
        </div>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            className="relative p-2 rounded-lg text-zinc-500 transition-all duration-200"
            style={{ background: 'transparent' }}
            onClick={() => {
              setNotifOpen((v) => !v);
              setUserMenuOpen(false);
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(225,6,0,0.08)';
              (e.currentTarget as HTMLElement).style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.color = '#71717a';
            }}
            aria-label="Notifications"
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span
                className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white px-0.5"
                style={{ background: '#E10600', boxShadow: '0 0 6px rgba(225,6,0,0.8)' }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            {unreadCount === 0 && !authLoading && (
              <span
                className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full opacity-0"
                style={{ background: '#E10600' }}
              />
            )}
          </button>

          {/* Notifications Dropdown */}
          {notifOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-80 rounded-xl overflow-hidden shadow-2xl"
              style={{
                background: 'rgba(18,18,18,0.98)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
              }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <span className="text-sm font-semibold text-white">Notifications</span>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs text-zinc-400 hover:text-white transition-colors"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setNotifOpen(false)}
                    className="text-zinc-500 hover:text-white transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

              <div className="max-h-72 overflow-y-auto scrollbar-thin">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-zinc-500 text-sm">
                    No notifications
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <button
                      key={notif.id}
                      onClick={() => handleNotifClick(notif)}
                      className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex gap-3 items-start"
                    >
                      <div
                        className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                        style={{
                          background: notif.is_read ? 'transparent' : '#E10600',
                          border: notif.is_read ? '1px solid rgba(255,255,255,0.15)' : 'none',
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-xs font-medium truncate ${
                            notif.is_read ? 'text-zinc-400' : 'text-white'
                          }`}
                        >
                          {notif.title}
                        </p>
                        {notif.body && (
                          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{notif.body}</p>
                        )}
                        <p className="text-[10px] text-zinc-600 mt-1">
                          {formatRelativeTime(notif.created_at)}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Avatar + Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            className="flex items-center gap-2 rounded-lg px-2 py-1 transition-all duration-200 hover:bg-white/5"
            onClick={() => {
              setUserMenuOpen((v) => !v);
              setNotifOpen(false);
            }}
            aria-label="User menu"
          >
            {/* Avatar */}
            {profile?.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt={profile.displayName}
                className="w-7 h-7 rounded-full object-cover"
                style={{ border: '1px solid rgba(225,6,0,0.3)' }}
              />
            ) : (
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'rgba(225,6,0,0.15)',
                  border: '1px solid rgba(225,6,0,0.3)',
                }}
              >
                {authLoading ? (
                  <span
                    className="w-3 h-3 rounded-full border border-t-transparent animate-spin"
                    style={{ borderColor: '#E10600', borderTopColor: 'transparent' }}
                  />
                ) : (
                  <span className="text-xs font-semibold" style={{ color: '#E10600' }}>
                    {profile?.initials ?? '?'}
                  </span>
                )}
              </div>
            )}

            {/* Name */}
            {!authLoading && profile && (
              <span className="text-xs text-zinc-300 font-medium max-w-[120px] truncate hidden sm:block">
                {profile.displayName}
              </span>
            )}
            {!authLoading && profile && (
              <ChevronDown size={12} className="text-zinc-500 hidden sm:block" />
            )}
          </button>

          {/* User Dropdown */}
          {userMenuOpen && profile && (
            <div
              className="absolute right-0 top-full mt-2 w-52 rounded-xl overflow-hidden shadow-2xl"
              style={{
                background: 'rgba(18,18,18,0.98)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
              }}
            >
              {/* Profile header */}
              <div className="px-4 py-3 border-b border-white/5">
                <p className="text-sm font-semibold text-white truncate">{profile.displayName}</p>
                <p className="text-xs text-zinc-500 capitalize">{profile.role}</p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    router.push('/profile');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <User size={14} />
                  Profile
                </button>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    router.push('/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <Settings size={14} />
                  Settings
                </button>
                <div className="border-t border-white/5 mt-1 pt-1">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/5 transition-colors"
                  >
                    <LogOut size={14} />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}